// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import { LinkViewElement, ViewElement } from '@simlin/core/datamodel';

import { Arrowhead } from '../Arrowhead';
import { Connector } from '../Connector';
import { ArrowheadRadius } from '../default';
import { Point } from '../common';
import { polarityGlyph } from '../../canvas-notation';

import styles from './CLDArrow.module.css';

/**
 * Tri-state polarity surfaced from the engine's link analysis.
 *
 * Kept as a plain string union (not the engine enum) so the diagram
 * package can use this type without depending on the engine's runtime
 * `LinkPolarity` symbol -- consumers translate `LinkPolarity` values
 * to these strings at the boundary.
 */
export type CLDArrowPolarity = 'positive' | 'negative' | 'unknown';

/** Back-off along the arrow tangent (ArrowheadRadius + glyph margin). */
export const BADGE_TANGENT_STANDOFF = ArrowheadRadius + 8;
/** Sideways step perpendicular to the line so the +/- clears the stroke. */
export const BADGE_PERP_OFFSET = 11;

/**
 * Build the canonical key used by Canvas's `linkPolarities` lookup
 * for a single causal link.  Both ends are identified by canonical
 * variable ident (the same form `Model.getLinks()` returns), so the
 * key is stable across project saves.  U+2192 RIGHTWARDS ARROW is
 * used as the separator instead of ASCII so it never collides with
 * legitimate ident characters.
 */
export function cldArrowKey(fromIdent: string, toIdent: string): string {
  return `${fromIdent}→${toIdent}`;
}

export interface CLDArrowProps {
  element: LinkViewElement;
  from: ViewElement;
  to: ViewElement;
  isSelected: boolean;
  polarity: CLDArrowPolarity;
  /** Optional pointer-drag arc override (matches the SFD Connector contract). */
  arcPoint?: Point;
  onSelection: (element: ViewElement, e: React.PointerEvent<SVGElement>, isArrowhead: boolean) => void;
}

/**
 * D2 acceptance criterion 16 -- CLD causal arrow.
 *
 * Renders a single LinkViewElement as a curved (or straight, when the
 * arc-takeoff angle aligns with the chord) causal arrow. Per the WS8a locked
 * notation (2026-07-30), the arrow body is NEUTRAL and SOLID regardless of
 * polarity -- color is not on the links; polarity reads from the +/- label at
 * the arrowhead (dashed is reserved for delay, never for negative).
 *
 * Per criterion 22, the geometry (path + arrowhead point + rotation)
 * comes from `Connector.computeArrowGeometry`, the same helper the
 * SFD `Connector` component uses.  This component never recomputes
 * arc circles, sweep direction, or element-edge intersections.
 *
 * Per criterion 24, the click / drag selection contract matches SFD:
 * arc-body pointerdown is treated as a link selection; arrowhead
 * pointerdown is treated as an arrowhead-target selection so the
 * existing reattach drag pipeline keeps working.  No new
 * infrastructure is introduced.
 */
export class CLDArrow extends React.PureComponent<CLDArrowProps> {
  handlePointerDownArc = (e: React.PointerEvent<SVGElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    this.props.onSelection(this.props.element, e, false);
  };

  handlePointerDownArrowhead = (e: React.PointerEvent<SVGElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    this.props.onSelection(this.props.element, e, true);
  };

  /**
   * Pick a screen position for the polarity badge.  Two offsets from
   * the arrowhead: back off along the tangent (`BADGE_TANGENT_STANDOFF`)
   * so the glyph clears the arrow's tip, and step perpendicular to the
   * line (`BADGE_PERP_OFFSET`) so the +/- reads *beside* the stroke
   * rather than sitting on top of it (otherwise the glyph blends into
   * the line and is hard to see).  The tangent back-off matches
   * ArrowheadRadius (6) plus a comfortable glyph margin.
   */
  private badgePosition(arrowhead: Point, arrowθDeg: number): Point {
    const θ = (arrowθDeg * Math.PI) / 180;
    // Perpendicular unit vector (θ + 90°): (-sinθ, cosθ).
    return {
      x: arrowhead.x - BADGE_TANGENT_STANDOFF * Math.cos(θ) - BADGE_PERP_OFFSET * Math.sin(θ),
      y: arrowhead.y - BADGE_TANGENT_STANDOFF * Math.sin(θ) + BADGE_PERP_OFFSET * Math.cos(θ),
    };
  }

  render(): React.ReactNode {
    const { element, from, to, isSelected, polarity, arcPoint } = this.props;

    const geometry = Connector.computeArrowGeometry({
      element,
      from,
      to,
      arcPoint,
      isSelected,
      onSelection: this.props.onSelection,
    });
    if (geometry === undefined) {
      // Degenerate geometry (collinear arc point) -- render nothing
      // rather than a broken path.  Matches the SFD Connector's
      // behaviour for the same input.
      return null;
    }

    const { path, arrowhead, arrowθ } = geometry;
    const badge = this.badgePosition(arrowhead, arrowθ);

    // Neutral, solid stroke for every polarity (locked notation); selection is the
    // only state that recolors the body.
    const strokeClass = isSelected ? styles.connectorSelected : styles.connector;

    return (
      <g
        key={element.uid}
        className={clsx('simlin-cld-arrow', `simlin-cld-arrow-${polarity}`)}
      >
        <path
          d={path}
          className={clsx(styles.connectorBg, 'simlin-connector-bg')}
          onPointerDown={this.handlePointerDownArc}
        />
        <path
          d={path}
          className={clsx(strokeClass, 'simlin-connector')}
          onPointerDown={this.handlePointerDownArc}
        />
        <Arrowhead
          point={arrowhead}
          angle={arrowθ}
          isSelected={isSelected}
          size={ArrowheadRadius}
          type="connector"
          onSelection={this.handlePointerDownArrowhead}
        />
        <text x={badge.x} y={badge.y} className={styles.polarityBadge}>
          {polarityGlyph(polarity)}
        </text>
      </g>
    );
  }
}
